'use client'

import { useState } from 'react'
import Link from 'next/link'
import { TopBar, BottomNav } from '@/components/layout/navigation'
import {
  Bell, Gift, Tag, Truck, Star, Check, Clock, Info,
  ChevronRight, Trash2, MessageCircle, Crown, Wallet
} from 'lucide-react'
import { toast } from 'sonner'

interface Notification {
  id: string
  type: 'system' | 'order' | 'promotion' | 'member'
  title: string
  content: string
  createdAt: string
  isRead: boolean
  link?: string
  icon: string
}

const INITIAL_NOTIFICATIONS: Notification[] = [
  {
    id: 'n1', type: 'promotion', title: '母亲节特惠活动开启',
    content: '母亲节期间指定蛋糕享买一送一，更多优惠等你来！活动时间：5月8日-5月12日。',
    createdAt: '2026-04-24T10:00:00', isRead: false, link: '/community', icon: '💐',
  },
  {
    id: 'n2', type: 'member', title: '恭喜！您获得了新人礼包',
    content: '注册成功，已为您发放100积分+¥50余额+新人专属优惠券，快来选购吧！',
    createdAt: '2026-04-24T09:00:00', isRead: false, link: '/coupons', icon: '🎉',
  },
  {
    id: 'n3', type: 'system', title: '平台服务升级通知',
    content: '为提供更优质的服务，平台将于4月25日凌晨2:00-4:00进行系统维护，届时部分功能可能暂时不可用。',
    createdAt: '2026-04-23T18:00:00', isRead: true, icon: '🔧',
  },
  {
    id: 'n4', type: 'promotion', title: '新品上市：抹茶千层蛋糕',
    content: '精选宇治抹茶+法式千层工艺，口感细腻丝滑，限时8折尝鲜！',
    createdAt: '2026-04-23T14:00:00', isRead: true, link: '/menu', icon: '🍵',
  },
  {
    id: 'n5', type: 'order', title: '订单配送完成',
    content: '您的订单 #ORD20260423001 已成功送达，感谢您的支持！如有问题请联系客服。',
    createdAt: '2026-04-23T12:00:00', isRead: true, link: '/profile', icon: '📦',
  },
  {
    id: 'n6', type: 'member', title: '签到提醒',
    content: '您已连续签到3天，再签4天可获得50积分大奖励！快来签到吧~',
    createdAt: '2026-04-23T08:00:00', isRead: true, link: '/checkin', icon: '📅',
  },
  {
    id: 'n7', type: 'system', title: '隐私政策更新',
    content: '我们更新了隐私政策和服务条款，请您抽空查看。继续使用本服务即表示同意更新后的条款。',
    createdAt: '2026-04-22T10:00:00', isRead: true, icon: '📋',
  },
]

const TYPE_CONFIG: Record<string, { label: string; color: string; bgColor: string }> = {
  all: { label: '全部', color: 'text-gray-500', bgColor: 'bg-gray-500' },
  system: { label: '系统', color: 'text-blue-500', bgColor: 'bg-blue-500' },
  order: { label: '订单', color: 'text-green-500', bgColor: 'bg-green-500' },
  promotion: { label: '活动', color: 'text-pink-500', bgColor: 'bg-pink-500' },
  member: { label: '会员', color: 'text-purple-500', bgColor: 'bg-purple-500' },
}

export default function NotificationsPage() {
  const [notifications, setNotifications] = useState<Notification[]>(INITIAL_NOTIFICATIONS)
  const [activeFilter, setActiveFilter] = useState('all')

  const filtered = activeFilter === 'all'
    ? notifications
    : notifications.filter(n => n.type === activeFilter)

  const unreadCount = notifications.filter(n => !n.isRead).length

  const markAsRead = (id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, isRead: true } : n))
  }

  const markAllRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
    toast.success('已全部标记为已读')
  }

  const deleteNotification = (id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id))
    toast.success('已删除')
  }

  const clearAll = () => {
    setNotifications([])
    toast.success('已清空全部通知')
  }

  return (
    <div className="min-h-screen pb-16 bg-gray-50">
      <TopBar title="通知中心" />

      {/* 顶部操作栏 */}
      <div className="mx-4 mt-3 bg-white rounded-xl p-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-pink-500" />
          <span className="text-sm font-bold text-gray-800">消息通知</span>
          {unreadCount > 0 && (
            <span className="px-1.5 py-0.5 bg-red-500 text-white text-[10px] font-bold rounded-full">{unreadCount}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button onClick={markAllRead} className="text-[10px] text-blue-500 flex items-center gap-0.5">
              <Check className="w-3 h-3" /> 全部已读
            </button>
          )}
          {notifications.length > 0 && (
            <button onClick={clearAll} className="text-[10px] text-gray-400 flex items-center gap-0.5">
              <Trash2 className="w-3 h-3" /> 清空
            </button>
          )}
        </div>
      </div>

      {/* 筛选 Tab */}
      <div className="mx-4 mt-2 bg-white rounded-xl p-1 flex gap-1">
        {Object.entries(TYPE_CONFIG).map(([key, config]) => (
          <button key={key} onClick={() => setActiveFilter(key)}
            className={`flex-1 py-2 rounded-lg text-xs font-medium transition-all ${
              activeFilter === key ? 'bg-pink-500 text-white' : 'text-gray-500'
            }`}
          >{config.label}</button>
        ))}
      </div>

      {/* 通知列表 */}
      <div className="mx-4 mt-2 mb-4">
        {filtered.length > 0 ? (
          <div className="space-y-1">
            {filtered.map(notification => {
              const typeConfig = TYPE_CONFIG[notification.type]
              return (
                <div
                  key={notification.id}
                  className={`bg-white rounded-xl overflow-hidden transition-all ${
                    !notification.isRead ? 'border-l-[3px] border-l-pink-500' : ''
                  }`}
                  onClick={() => markAsRead(notification.id)}
                >
                  {notification.link ? (
                    <Link href={notification.link} className="block p-3 flex items-start gap-3">
                      <div className="w-9 h-9 bg-gray-50 rounded-full flex items-center justify-center text-lg flex-shrink-0 mt-0.5">
                        {notification.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate ${!notification.isRead ? 'font-bold text-gray-800' : 'text-gray-600'}`}>
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <span className="w-1.5 h-1.5 bg-pink-500 rounded-full flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{notification.content}</p>
                        <p className="text-[10px] text-gray-300 mt-1">
                          {new Date(notification.createdAt).toLocaleString('zh-CN', {
                            month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={e => { e.preventDefault(); deleteNotification(notification.id) }}
                          className="p-1 text-gray-300 hover:text-red-400"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                        <ChevronRight className="w-4 h-4 text-gray-300" />
                      </div>
                    </Link>
                  ) : (
                    <div className="p-3 flex items-start gap-3">
                      <div className="w-9 h-9 bg-gray-50 rounded-full flex items-center justify-center text-lg flex-shrink-0 mt-0.5">
                        {notification.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className={`text-sm truncate ${!notification.isRead ? 'font-bold text-gray-800' : 'text-gray-600'}`}>
                            {notification.title}
                          </p>
                          {!notification.isRead && (
                            <span className="w-1.5 h-1.5 bg-pink-500 rounded-full flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{notification.content}</p>
                        <p className="text-[10px] text-gray-300 mt-1">
                          {new Date(notification.createdAt).toLocaleString('zh-CN', {
                            month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                      <button
                        onClick={() => deleteNotification(notification.id)}
                        className="p-1 text-gray-300 hover:text-red-400 flex-shrink-0"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl p-8 text-center">
            <Bell className="w-8 h-8 text-gray-200 mx-auto mb-2" />
            <p className="text-xs text-gray-400">暂无{activeFilter === 'all' ? '' : TYPE_CONFIG[activeFilter]?.label}通知</p>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  )
}
